<template>
  <template v-if="hasText">
    <div
      ref="reference"
      @mouseenter="handleMouseEnter"
      @mouseleave="handleMouseLeave"
      @mousemove="handleMouseMove"
      @focusin="handleFocusIn"
      @focusout="handleFocusOut"
      :tabindex="focusable ? 0 : -1"
      :aria-label="ariaLabel && `Tooltip: ${ariaLabel}`"
      :aria-describedby="tooltipId"
      class="inline-block focus:outline-none"
      :class="class"
    >
      <slot />
      <span class="sr-only"> Tooltip contents: {{ text }} </span>
    </div>

    <!-- Gated so a mounted Teleport does not park anchor nodes in the body per
         table row, but held open across the fade so the leave still runs. -->
    <teleport v-if="panelMounted" to="body">
      <transition name="fade" appear>
        <div
          v-if="open"
          ref="floating"
          :id="tooltipId"
          role="tooltip"
          :style="followCursor && !openedByFocus ? cursorStyles : floatingStyles"
          :class="[
            FLOATING_PANEL_CLS,
            'absolute z-50 max-w-xs whitespace-pre-line text-foreground',
            align === 'center' ? 'text-center' : 'text-left',
            followCursor ? 'pointer-events-none' : 'pointer-events-auto',
            panelClass,
          ]"
          @mouseenter="handleFloatingEnter"
          @mouseleave="handleFloatingLeave"
          @focusin="handleFloatingEnter"
          @focusout="handleFloatingLeave"
        >
          <!-- Rich content takes over the visual tooltip; `text` stays the
               accessible/sr-only representation either way. -->
          <slot name="content">{{ text }}</slot>
        </div>
      </transition>
    </teleport>
  </template>

  <!-- This avoids weird accessibility issues with tabindex -->
  <template v-else>
    <slot />
  </template>
</template>

<script setup>
import { ref, watchEffect, computed } from "vue";
import { useFloating, offset, shift, flip, autoUpdate } from "@floating-ui/vue";
import { FLOATING_PANEL_CLS } from "./floatingPanelClasses";
import { provideTooltipActive, useInsideActiveTooltip } from "./tooltipContext";

const props = defineProps({
  ariaLabel: { type: String, required: false },
  text: { type: String, default: "" },
  class: { type: [String, Array, Object], default: "" },
  followCursor: { type: Boolean, default: false },
  delay: { type: Number, default: 200 },
  // Text alignment inside the panel.
  align: {
    type: String,
    default: "left",
    validator: (value) => ["left", "center"].includes(value),
  },
  side: {
    type: String,
    default: "top",
    validator: (value) => ["top", "bottom", "left", "right"].includes(value),
  },
  // Padding and text size for the floating panel. Overriding replaces them, so a
  // caller can match another surface (HoverTime matches the IP popover's card).
  panelClass: { type: [String, Array, Object], default: "px-2 py-1.5 text-xs" },
  focusable: {
    type: Boolean,
    default: false,
  },
  childFocusShowsTooltip: {
    type: Boolean,
    default: false,
  },
});

const open = ref(false);
const reference = ref(null);
const floating = ref(null);
const mousePosition = ref({ x: 0, y: 0 });
const hoverTimeout = ref(null);
const tooltipId = `tooltip-${Math.random().toString(36).slice(2, 8)}`;
const openedByFocus = ref(false); // track if focus opened the tooltip

const hasText = computed(() => props.text?.trim()?.length > 0);

// Mirrors `open`, but lags it by the fade so the teleport is still mounted while
// the panel animates out. Idle tooltips still cost nothing.
const FADE_MS = 150;
const panelMounted = ref(false);
const unmountTimeout = ref(null);
watchEffect(() => {
  if (unmountTimeout.value) {
    clearTimeout(unmountTimeout.value);
    unmountTimeout.value = null;
  }
  if (open.value) {
    panelMounted.value = true;
  } else if (panelMounted.value) {
    unmountTimeout.value = setTimeout(() => {
      panelMounted.value = false;
    }, FADE_MS);
  }
});

// Publish "a tooltip is live here", ORed with what we inherited, so a nested one can suppress itself.
const outerTooltipActive = useInsideActiveTooltip();
provideTooltipActive(computed(() => hasText.value || outerTooltipActive.value));

const { floatingStyles, update } = useFloating(reference, floating, {
  placement: props.side,
  // Padded shift so a panel near the window edge keeps a margin instead of
  // sitting flush against it.
  middleware: [offset(8), shift({ padding: 8 }), flip()],
});

const cursorStyles = computed(() => ({
  position: "fixed",
  left: `${mousePosition.value.x + 10}px`,
  top: `${mousePosition.value.y - 10}px`,
  transform: "none",
}));

// Grace period after leaving the trigger, long enough for the pointer to
// travel into the tooltip panel (which keeps it open while hovered).
const CLOSE_GRACE_MS = 150;
const closeTimeout = ref(null);

const cancelClose = () => {
  if (closeTimeout.value) {
    clearTimeout(closeTimeout.value);
    closeTimeout.value = null;
  }
};

const handleMouseEnter = () => {
  if (!hasText.value) return;
  cancelClose();
  openedByFocus.value = false;
  if (props.delay > 0) {
    hoverTimeout.value = setTimeout(() => {
      open.value = true;
    }, props.delay);
  } else {
    open.value = true;
  }
};

const handleMouseLeave = () => {
  if (hoverTimeout.value) {
    clearTimeout(hoverTimeout.value);
    hoverTimeout.value = null;
  }
  cancelClose();
  closeTimeout.value = setTimeout(() => {
    open.value = false;
  }, CLOSE_GRACE_MS);
};

// Hovering the panel itself keeps it open (except in follow-cursor mode,
// where the panel tracks the pointer and never receives events).
const handleFloatingEnter = () => {
  cancelClose();
};

const handleFloatingLeave = () => {
  cancelClose();
  open.value = false;
};

const handleMouseMove = (event) => {
  if (props.followCursor) {
    openedByFocus.value = false;
    mousePosition.value = { x: event.clientX, y: event.clientY };
  }
};

const handleFocusIn = () => {
  if (!hasText.value) return;
  if (!props.focusable && !props.childFocusShowsTooltip) return;
  open.value = true;
  openedByFocus.value = true;
  if (reference.value && floating.value) {
    update();
  }
};

const handleFocusOut = () => {
  open.value = false;
  openedByFocus.value = false;
};

const handleKeydown = (e) => {
  if (e.key === "Escape") {
    open.value = false;
    openedByFocus.value = false;
  }
};

watchEffect((onCleanup) => {
  if (open.value) {
    window.addEventListener("keydown", handleKeydown);
    onCleanup(() => window.removeEventListener("keydown", handleKeydown));
  }
});

// autoUpdate only if not following cursor OR opened by focus
watchEffect((onCleanup) => {
  if (
    reference.value &&
    floating.value &&
    open.value &&
    hasText.value &&
    (!props.followCursor || openedByFocus.value)
  ) {
    const cleanup = autoUpdate(reference.value, floating.value, update);
    onCleanup(cleanup);
  }
});
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.15s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
